import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { useTeleportOptional } from '@/contexts/TeleportContext';
import { getSocket } from '@/hooks/useRealtimeSync';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { useClickOutside } from '@/hooks/useClickOutside';
import { api, type MessengerMessage } from '@/api/client';
import type {
  ChatAttachedProjectPayload,
  ChatAttachedSessionPayload,
  DirectComposerPendingFile,
} from '@/components/Messenger/types/chatMessageTypes';
import {
  MESSENGER_DM_FILE_STILL_TOO_LARGE_AFTER_PROCESS,
  MESSENGER_DM_ONLY_ONE_FILE_ALLOWED,
  MESSENGER_DM_PREPARE_FILE_FAILED,
  MESSENGER_DM_SINGLE_FILE_SLOT_OCCUPIED,
  MESSENGER_EXCLUSIVE_ATTACHMENTS_HINT,
  MESSENGER_DM_NO_ATTACH_WHILE_FORWARD,
  MESSENGER_MAX_FILE_BYTES,
  messengerDmFileTooLargeMessage,
} from '@/components/Messenger/shared/composer/messengerAttachmentLimits';
import {
  contextAttachmentsForDirectApi,
  mergeChatContextAttachOneSlot,
  readChatAttachFromDataTransfer,
} from '@/components/Messenger/shared/composer/readChatAttachFromDataTransfer';
import { contextAttachmentsForForwardPayload } from '@/components/Messenger/shared/contextAttachmentsForForward';
import { prepareDirectComposerFilesForUpload } from '@/components/Messenger/shared/composer/prepareDirectComposerFilesForUpload';
import type { MessengerImageSendModeChoice } from '@/components/Messenger/shared/composer/MessengerImageSendModeModal';
import {
  getDirectThreadSnapshot,
  subscribeDirectThread,
  subscribeDirectThreadMeta,
  type DirectThreadAtomicOwnPayload,
  type DirectThreadForwardSendPayload,
} from '@/components/Messenger/cache/directThreadCacheStore';
import {
  getMessengerDraft,
  removeMessengerDraftFromStore,
  removeMessengerDraftFromStoreAtRevision,
  subscribeMessengerDraft,
  upsertMessengerDraftInStore,
} from '@/components/Messenger/cache/messengerDraftsStore';
import {
  isComposerDraftSnapshotEmpty,
  MessengerDraftSaveScheduler,
  shouldApplyRemoteDraftToComposer,
  type ComposerDraftSnapshot,
} from '@/components/Messenger/shared/messengerComposerDraftSync';
import { MESSENGER_EMPTY_FORWARD_DRAFT } from '@/constants/messengerForwardDraft';
import {
  createThreadComposerEditStash,
  resolveThreadComposerDraftState,
  type ThreadComposerDraftReferenceIds,
  type ThreadComposerEditStash,
} from './threadComposerDraftState';

function draftSnapshotSignature(snapshot: ComposerDraftSnapshot): string {
  return JSON.stringify([
    snapshot.text,
    snapshot.reply_to_message_id,
    snapshot.forward_message_ids,
  ]);
}

interface UseThreadComposerOptions {
  threadId: string;
  sendMessage: (data: {
    body?: string;
    context_attachments?: unknown;
    localImageFiles?: File[];
    file_url?: string;
    file_name?: string;
    file_size?: number;
    mime_type?: string;
    reply_to_id?: string;
    forwarded_from_message_id?: string;
  }) => Promise<MessengerMessage | undefined | void>;
  /** Атомарно на сервере: своё сообщение (опционально) + пересланные в одной транзакции БД */
  sendOwnAndForwardedAtomic: (args: {
    own?: DirectThreadAtomicOwnPayload;
    forwards: Array<{
      apiPayload: DirectThreadForwardSendPayload;
      forwarded_from_sender_name: string;
    }>;
  }) => Promise<MessengerMessage[]>;
  /** Имя автора оригинала для заголовка «Переслано от …» в оптимистичном бабле. */
  resolveForwardAuthorName: (msg: MessengerMessage) => string;
  /** Сохранение отредактированного текста (личка) */
  editMessage?: (messageId: string, body: string) => Promise<void>;
  composerInputRef: React.RefObject<HTMLTextAreaElement>;
  initialForwardMsgs?: MessengerMessage[];
  /** Сбросить черновик пересылки в ChatShell (pendingForwardsByThread) */
  onConsumePendingForwardDraft?: () => void;
  /** Сообщение, которое стало последним в треде после локальной отправки. */
  onMessageCommitted?: (msg: MessengerMessage) => void;
}

export function useThreadComposer({
  threadId,
  sendMessage,
  sendOwnAndForwardedAtomic,
  resolveForwardAuthorName,
  editMessage,
  composerInputRef,
  initialForwardMsgs,
  onConsumePendingForwardDraft,
  onMessageCommitted,
}: UseThreadComposerOptions) {
  const { showError: showToastError } = useToast();
  const teleport = useTeleportOptional();

  const [input, setInput] = useState('');
  const [attachedProjects, setAttachedProjects] = useState<ChatAttachedProjectPayload[]>([]);
  const [attachedSessions, setAttachedSessions] = useState<ChatAttachedSessionPayload[]>([]);
  const [composerFiles, setComposerFiles] = useState<DirectComposerPendingFile[]>([]);
  const [pendingImagePick, setPendingImagePick] = useState<File[] | null>(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [replyTo, setReplyToState] = useState<MessengerMessage | null>(null);
  const [forwardMsgs, setForwardMsgsState] = useState<MessengerMessage[]>(() =>
    initialForwardMsgs?.length ? [...initialForwardMsgs] : []
  );
  const [editingMessage, setEditingMessage] = useState<MessengerMessage | null>(null);
  const [hydratedThreadId, setHydratedThreadId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const composerDraftBeforeEditRef = useRef<ThreadComposerEditStash | null>(null);
  const isComposerFocusedRef = useRef(false);
  const localDraftRevisionMsRef = useRef(0);
  const draftTargetThreadIdRef = useRef(threadId);
  const threadHydrationGenerationRef = useRef(0);
  const lastSyncedDraftSignatureRef = useRef<string | null>(null);
  const draftReferenceIdsRef = useRef<ThreadComposerDraftReferenceIds>({
    replyToMessageId: null,
    forwardMessageIds: initialForwardMsgs?.map((message) => message.id) ?? [],
  });

  const replyToRef = useRef<MessengerMessage | null>(null);
  replyToRef.current = replyTo;
  const editingMessageRef = useRef<MessengerMessage | null>(null);
  editingMessageRef.current = editingMessage;
  const forwardMsgsRef = useRef<MessengerMessage[]>([]);
  forwardMsgsRef.current = forwardMsgs;

  const blockComposerAttachIfForwardDraft = useCallback((): boolean => {
    if (forwardMsgsRef.current.length === 0) return false;
    showToastError(MESSENGER_DM_NO_ATTACH_WHILE_FORWARD);
    return true;
  }, [showToastError]);

  const clearForwardMsgs = useCallback(() => {
    draftReferenceIdsRef.current.forwardMessageIds = [];
    setForwardMsgsState([]);
    onConsumePendingForwardDraft?.();
  }, [onConsumePendingForwardDraft]);

  const setForwardMsgs = useCallback((messages: MessengerMessage[]) => {
    draftReferenceIdsRef.current.forwardMessageIds = messages.map((message) => message.id);
    setForwardMsgsState(messages);
  }, []);

  const setReplyTo = useCallback((message: MessengerMessage) => {
    draftReferenceIdsRef.current.replyToMessageId = message.id;
    setReplyToState(message);
  }, []);

  const clearReplyTo = useCallback(() => {
    draftReferenceIdsRef.current.replyToMessageId = null;
    setReplyToState(null);
  }, []);

  const restoreComposerDraftAfterEdit = useCallback(() => {
    const stashed = composerDraftBeforeEditRef.current;
    composerDraftBeforeEditRef.current = null;
    setEditingMessage(null);
    if (stashed === null) return;
    draftReferenceIdsRef.current = {
      replyToMessageId: stashed.referenceIds.replyToMessageId,
      forwardMessageIds: [...stashed.referenceIds.forwardMessageIds],
    };
    setInput(stashed.text);
    setReplyToState(stashed.replyTo);
    setForwardMsgsState([...stashed.forwardMessages]);
  }, []);

  const clearEditingMessage = useCallback(() => {
    restoreComposerDraftAfterEdit();
  }, [restoreComposerDraftAfterEdit]);

  const beginEditMessage = useCallback((msg: MessengerMessage) => {
    if (msg.forwarded_from_message_id) return;
    if (msg.forwarded_from_label?.trim()) return;
    if (editingMessageRef.current === null) {
      composerDraftBeforeEditRef.current = createThreadComposerEditStash({
        text: inputRef.current,
        replyTo: replyToRef.current,
        forwardMessages: forwardMsgsRef.current,
        referenceIds: draftReferenceIdsRef.current,
      });
    }
    setReplyToState(null);
    if (forwardMsgsRef.current.length > 0) {
      onConsumePendingForwardDraft?.();
    }
    setForwardMsgsState([]);
    setComposerFiles([]);
    setAttachedProjects([]);
    setAttachedSessions([]);
    setAttachMenuOpen(false);
    setPendingImagePick(null);
    if (imageFileInputRef.current) imageFileInputRef.current.value = '';
    if (anyFileInputRef.current) anyFileInputRef.current.value = '';
    setInput(msg.body ?? '');
    setEditingMessage(msg);
  }, [onConsumePendingForwardDraft]);

  const inputRef = useRef(input);
  inputRef.current = input;
  const composerFilesRef = useRef(composerFiles);
  composerFilesRef.current = composerFiles;
  const attachedProjectsRef = useRef(attachedProjects);
  const attachedSessionsRef = useRef(attachedSessions);
  attachedProjectsRef.current = attachedProjects;
  attachedSessionsRef.current = attachedSessions;
  const pendingImagePickRef = useRef<File[] | null>(null);
  pendingImagePickRef.current = pendingImagePick;

  const draftSchedulerRef = useRef<MessengerDraftSaveScheduler | null>(null);
  if (draftSchedulerRef.current === null) {
    draftSchedulerRef.current = new MessengerDraftSaveScheduler({
      save: async (snapshot, clientUpdatedAt) => {
        const targetId = draftTargetThreadIdRef.current;
        const result = await api.messenger.putDraft('thread', targetId, {
          ...snapshot,
          client_updated_at: clientUpdatedAt,
        });
        if (result.draft) {
          upsertMessengerDraftInStore(result.draft);
        } else {
          removeMessengerDraftFromStoreAtRevision('thread', targetId, clientUpdatedAt);
        }
      },
      clear: async (clientUpdatedAt) => {
        const targetId = draftTargetThreadIdRef.current;
        removeMessengerDraftFromStoreAtRevision('thread', targetId, clientUpdatedAt);
        const result = await api.messenger.deleteDraft('thread', targetId, clientUpdatedAt);
        if (result.draft) {
          upsertMessengerDraftInStore(result.draft);
        }
      },
    });
  }
  const draftScheduler = draftSchedulerRef.current;

  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const anyFileInputRef = useRef<HTMLInputElement>(null);
  const attachMenuRef = useRef<HTMLButtonElement>(null);
  const attachPopoverRef = useRef<HTMLDivElement>(null);

  const typingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emitTyping = useCallback(() => {
    if (!threadId) return;
    getSocket().emit('messenger:typing', { threadId });
    if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    typingDebounceRef.current = setTimeout(() => {
      typingDebounceRef.current = null;
    }, 2000);
  }, [threadId]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInput(value);
      if (value.trim() && typingDebounceRef.current == null) {
        emitTyping();
      }
    },
    [emitTyping]
  );

  useEffect(() => {
    return () => {
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    };
  }, []);

  const {
    shouldRender: attachMenuShouldRender,
    contentClasses: attachMenuContentClasses,
    requestClose: attachMenuRequestClose,
  } = useOverlayAnimation(attachMenuOpen, () => setAttachMenuOpen(false), true);

  usePopoverPositioning({
    triggerRef: attachMenuRef,
    popoverRef: attachPopoverRef,
    isOpen: attachMenuShouldRender,
    preferredPositionY: 'top',
    priorityAxis: 'y',
    horizontalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1100,
  });

  useClickOutside(attachPopoverRef, attachMenuRequestClose, {
    enabled: attachMenuShouldRender,
    capture: false,
    excludeRefs: [attachMenuRef],
    excludeSelector: '.context-menu',
  });

  const initialForwardMsgsRef = useRef(initialForwardMsgs ?? MESSENGER_EMPTY_FORWARD_DRAFT);
  const prevInitialForwardMsgsRef = useRef(initialForwardMsgs ?? MESSENGER_EMPTY_FORWARD_DRAFT);

  // Новый телепорт пересылки в тот же тред или очистка в родителе после отправки/отмены
  useEffect(() => {
    const prev = prevInitialForwardMsgsRef.current;
    const next = initialForwardMsgs ?? MESSENGER_EMPTY_FORWARD_DRAFT;
    if (next === prev) return;
    prevInitialForwardMsgsRef.current = next;
    draftReferenceIdsRef.current.forwardMessageIds = next.map((message) => message.id);
    setForwardMsgsState(next.length > 0 ? [...next] : []);
  }, [initialForwardMsgs]);

  initialForwardMsgsRef.current = initialForwardMsgs ?? MESSENGER_EMPTY_FORWARD_DRAFT;

  useEffect(() => {
    const generation = ++threadHydrationGenerationRef.current;
    setHydratedThreadId(null);

    void (async () => {
      await draftScheduler.flush();
      if (generation !== threadHydrationGenerationRef.current) return;

      draftTargetThreadIdRef.current = threadId;
      composerDraftBeforeEditRef.current = null;
      draftReferenceIdsRef.current = {
        replyToMessageId: null,
        forwardMessageIds: [],
      };
      setInput('');
      setAttachedProjects([]);
      setAttachedSessions([]);
      setComposerFiles([]);
      setPendingImagePick(null);
      setAttachMenuOpen(false);
      setReplyToState(null);
      setForwardMsgsState([]);
      setEditingMessage(null);
      if (imageFileInputRef.current) imageFileInputRef.current.value = '';
      if (anyFileInputRef.current) anyFileInputRef.current.value = '';

      const draft = getMessengerDraft('thread', threadId);
      const cacheSnapshot = getDirectThreadSnapshot(threadId);
      const resolved = resolveThreadComposerDraftState({
        draft,
        cachedMessages: cacheSnapshot.messages,
        cacheHydrated: cacheSnapshot.hydrated,
        pendingForwardMessages: initialForwardMsgsRef.current,
      });
      const storedSnapshot: ComposerDraftSnapshot = {
        text: draft?.text ?? '',
        reply_to_message_id: draft?.reply_to_message_id ?? null,
        forward_message_ids: draft?.forward_message_ids ?? [],
      };
      lastSyncedDraftSignatureRef.current = draftSnapshotSignature(storedSnapshot);
      localDraftRevisionMsRef.current = draft ? Date.parse(draft.updated_at) || 0 : 0;
      draftReferenceIdsRef.current = resolved.referenceIds;
      setInput(resolved.text);
      setReplyToState(resolved.replyTo);
      setForwardMsgsState(resolved.forwardMessages);
      setHydratedThreadId(threadId);
    })();

    return () => {
      threadHydrationGenerationRef.current += 1;
    };
  }, [draftScheduler, threadId]);

  useEffect(() => {
    if (hydratedThreadId !== threadId || editingMessage !== null || isSubmitting) return;

    const snapshot: ComposerDraftSnapshot = {
      text: input,
      reply_to_message_id: draftReferenceIdsRef.current.replyToMessageId,
      forward_message_ids: [...draftReferenceIdsRef.current.forwardMessageIds],
    };
    const signature = draftSnapshotSignature(snapshot);
    if (signature === lastSyncedDraftSignatureRef.current) return;

    const clientUpdatedAt = draftScheduler.schedule(snapshot);
    lastSyncedDraftSignatureRef.current = signature;
    localDraftRevisionMsRef.current = Date.parse(clientUpdatedAt);
    if (isComposerDraftSnapshotEmpty(snapshot)) {
      removeMessengerDraftFromStore('thread', threadId);
      return;
    }
    upsertMessengerDraftInStore(
      {
        target_kind: 'thread',
        target_id: threadId,
        ...snapshot,
        updated_at: clientUpdatedAt,
      },
      { source: 'local' }
    );
  }, [
    draftScheduler,
    editingMessage,
    forwardMsgs,
    hydratedThreadId,
    input,
    isSubmitting,
    replyTo,
    threadId,
  ]);

  useEffect(() => {
    if (hydratedThreadId !== threadId) return;

    const reconcileDraftReferences = () => {
      const cacheSnapshot = getDirectThreadSnapshot(threadId);
      const messagesById = new Map<string, MessengerMessage>();
      for (const message of cacheSnapshot.messages) messagesById.set(message.id, message);
      if (replyToRef.current) messagesById.set(replyToRef.current.id, replyToRef.current);
      for (const message of forwardMsgsRef.current) messagesById.set(message.id, message);

      const referenceIds = draftReferenceIdsRef.current;
      const nextReply = referenceIds.replyToMessageId
        ? (messagesById.get(referenceIds.replyToMessageId) ?? null)
        : null;
      const nextForwards = referenceIds.forwardMessageIds.flatMap((id) => {
        const message = messagesById.get(id);
        return message ? [message] : [];
      });

      setReplyToState(nextReply);
      setForwardMsgsState(nextForwards);
    };

    reconcileDraftReferences();
    const unsubscribeContent = subscribeDirectThread(threadId, reconcileDraftReferences);
    const unsubscribeMeta = subscribeDirectThreadMeta(threadId, reconcileDraftReferences);
    return () => {
      unsubscribeContent();
      unsubscribeMeta();
    };
  }, [hydratedThreadId, threadId]);

  const applyStoredDraftIfAllowed = useCallback(() => {
    if (hydratedThreadId !== threadId) return;
    const draft = getMessengerDraft('thread', threadId);
    const remoteUpdatedAtMs = draft ? Date.parse(draft.updated_at) || 0 : Date.now();
    if (
      !shouldApplyRemoteDraftToComposer({
        isComposerFocused: isComposerFocusedRef.current,
        isEditingMessage: editingMessageRef.current !== null,
        localRevisionMs: localDraftRevisionMsRef.current,
        remoteUpdatedAtMs,
      })
    ) {
      return;
    }

    const cacheSnapshot = getDirectThreadSnapshot(threadId);
    const resolved = resolveThreadComposerDraftState({
      draft,
      cachedMessages: cacheSnapshot.messages,
      cacheHydrated: cacheSnapshot.hydrated,
      pendingForwardMessages: [],
    });
    lastSyncedDraftSignatureRef.current = draftSnapshotSignature({
      text: draft?.text ?? '',
      reply_to_message_id: draft?.reply_to_message_id ?? null,
      forward_message_ids: draft?.forward_message_ids ?? [],
    });
    localDraftRevisionMsRef.current = remoteUpdatedAtMs;
    draftReferenceIdsRef.current = resolved.referenceIds;
    setInput(resolved.text);
    setReplyToState(resolved.replyTo);
    setForwardMsgsState(resolved.forwardMessages);
  }, [hydratedThreadId, threadId]);

  useEffect(() => {
    return subscribeMessengerDraft('thread', threadId, applyStoredDraftIfAllowed);
  }, [applyStoredDraftIfAllowed, threadId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') void draftScheduler.flush();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [draftScheduler]);

  useEffect(() => {
    return () => draftScheduler.dispose();
  }, [draftScheduler]);

  useEffect(() => {
    if (composerFiles.length > 0 && attachMenuOpen) setAttachMenuOpen(false);
  }, [composerFiles.length, attachMenuOpen]);

  const queueImagesForSendModeChoice = useCallback(
    (newFiles: File[]) => {
      const images = newFiles.filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) return;
      if (blockComposerAttachIfForwardDraft()) return;
      if (composerFilesRef.current.length > 0) {
        showToastError(MESSENGER_DM_SINGLE_FILE_SLOT_OCCUPIED);
        return;
      }
      if (images.length > 1) {
        showToastError(MESSENGER_DM_ONLY_ONE_FILE_ALLOWED);
        return;
      }
      const img = images[0]!;
      if (img.size > MESSENGER_MAX_FILE_BYTES) {
        showToastError(messengerDmFileTooLargeMessage(img.name));
        return;
      }
      setAttachedProjects([]);
      setAttachedSessions([]);
      setPendingImagePick([img]);
    },
    [showToastError, blockComposerAttachIfForwardDraft]
  );

  const addFilesAsFilePicker = useCallback(
    (newFiles: File[]) => {
      if (newFiles.length === 0) return;
      if (blockComposerAttachIfForwardDraft()) return;
      if (newFiles.length > 1) {
        showToastError(MESSENGER_DM_ONLY_ONE_FILE_ALLOWED);
        return;
      }
      if (composerFilesRef.current.length > 0) {
        showToastError(MESSENGER_DM_SINGLE_FILE_SLOT_OCCUPIED);
        return;
      }
      const file = newFiles[0]!;
      if (file.size > MESSENGER_MAX_FILE_BYTES) {
        showToastError(messengerDmFileTooLargeMessage(file.name));
        return;
      }
      setAttachedProjects([]);
      setAttachedSessions([]);
      setComposerFiles([{ source: 'add_file' as const, file }]);
    },
    [showToastError, blockComposerAttachIfForwardDraft]
  );

  const addPhotosFromPicker = useCallback(
    (newFiles: File[]) => {
      const images = newFiles.filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) return;
      if (blockComposerAttachIfForwardDraft()) return;
      if (composerFilesRef.current.length > 0) {
        showToastError(MESSENGER_DM_SINGLE_FILE_SLOT_OCCUPIED);
        return;
      }
      if (images.length > 1) {
        showToastError(MESSENGER_DM_ONLY_ONE_FILE_ALLOWED);
        return;
      }
      const file = images[0]!;
      if (file.size > MESSENGER_MAX_FILE_BYTES) {
        showToastError(messengerDmFileTooLargeMessage(file.name));
        return;
      }
      setAttachedProjects([]);
      setAttachedSessions([]);
      setComposerFiles([{ source: 'add_photo' as const, file }]);
    },
    [showToastError, blockComposerAttachIfForwardDraft]
  );

  const removeFile = useCallback((index: number) => {
    setComposerFiles((prev) => prev.filter((_, i) => i !== index));
    if (imageFileInputRef.current) imageFileInputRef.current.value = '';
    if (anyFileInputRef.current) anyFileInputRef.current.value = '';
  }, []);

  const handleImageSendModeChosen = useCallback(
    (mode: MessengerImageSendModeChoice) => {
      const prev = pendingImagePickRef.current;
      const file = prev?.[0];
      if (!file) return;
      if (blockComposerAttachIfForwardDraft()) {
        setPendingImagePick(null);
        return;
      }
      if (file.size > MESSENGER_MAX_FILE_BYTES) {
        showToastError(messengerDmFileTooLargeMessage(file.name));
        setPendingImagePick(null);
        return;
      }
      const sendAsAttachment = mode === 'attachment';
      setAttachedProjects([]);
      setAttachedSessions([]);
      setComposerFiles([{ source: 'paste_or_drop' as const, file, sendAsAttachment }]);
      setPendingImagePick(null);
    },
    [showToastError, blockComposerAttachIfForwardDraft]
  );

  const dismissImageSendModeModal = useCallback(() => {
    setPendingImagePick(null);
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (editingMessageRef.current) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        if (blockComposerAttachIfForwardDraft()) return;
        if (composerFilesRef.current.length > 0) {
          showToastError(MESSENGER_DM_SINGLE_FILE_SLOT_OCCUPIED);
          return;
        }
        queueImagesForSendModeChoice(imageFiles);
      }
    },
    [queueImagesForSendModeChoice, showToastError, blockComposerAttachIfForwardDraft]
  );

  const clearPersistedDraft = useCallback(async () => {
    await draftScheduler.discard();
    lastSyncedDraftSignatureRef.current = draftSnapshotSignature({
      text: '',
      reply_to_message_id: null,
      forward_message_ids: [],
    });
    localDraftRevisionMsRef.current = Date.now();
    const clientUpdatedAt = new Date(localDraftRevisionMsRef.current).toISOString();
    removeMessengerDraftFromStoreAtRevision('thread', threadId, clientUpdatedAt);
    try {
      const result = await api.messenger.deleteDraft('thread', threadId, clientUpdatedAt);
      if (result.draft) upsertMessengerDraftInStore(result.draft);
    } catch {
      // Sending succeeded; draft cleanup remains best effort and reconciles on reconnect.
    }
  }, [draftScheduler, threadId]);

  const handleComposerFocus = useCallback(() => {
    isComposerFocusedRef.current = true;
  }, []);

  const handleComposerBlur = useCallback(() => {
    isComposerFocusedRef.current = false;
    applyStoredDraftIfAllowed();
    void draftScheduler.flush();
  }, [applyStoredDraftIfAllowed, draftScheduler]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const text = inputRef.current.trim();
      const editTarget = editingMessageRef.current;
      if (editTarget) {
        const prevBody = (editTarget.body ?? '').trim();
        const hasMedia = Boolean(
          editTarget.file_url && String(editTarget.file_url).trim() !== ''
        );
        if (!text && !hasMedia) return;
        if (text === prevBody) {
          restoreComposerDraftAfterEdit();
          requestAnimationFrame(() => {
            composerInputRef.current?.focus();
          });
          return;
        }
        try {
          await editMessage?.(editTarget.id, text);
          restoreComposerDraftAfterEdit();
        } catch {
          /* ошибка — тост из кэша */
        } finally {
          requestAnimationFrame(() => {
            composerInputRef.current?.focus();
          });
        }
        return;
      }

      const ctxPayload = contextAttachmentsForDirectApi(
        attachedProjectsRef.current,
        attachedSessionsRef.current
      );
      const fwdMsgs = forwardMsgsRef.current;
      if (!text && composerFilesRef.current.length === 0 && ctxPayload.length === 0 && fwdMsgs.length === 0) return;

      const hasFileSlot = composerFilesRef.current.length > 0;
      const hasCtxSlot = ctxPayload.length > 0;
      if (hasFileSlot && hasCtxSlot) {
        showToastError(MESSENGER_EXCLUSIVE_ATTACHMENTS_HINT);
        return;
      }

      for (const item of composerFilesRef.current) {
        if (item.file.size > MESSENGER_MAX_FILE_BYTES) {
          showToastError(messengerDmFileTooLargeMessage(item.file.name));
          return;
        }
      }

      let filesToSend: File[] = [];
      if (composerFilesRef.current.length > 0) {
        try {
          filesToSend = await prepareDirectComposerFilesForUpload(composerFilesRef.current);
        } catch {
          showToastError(MESSENGER_DM_PREPARE_FILE_FAILED);
          return;
        }
        for (const f of filesToSend) {
          if (f.size > MESSENGER_MAX_FILE_BYTES) {
            showToastError(MESSENGER_DM_FILE_STILL_TOO_LARGE_AFTER_PROCESS);
            return;
          }
        }
      }

      const replyId = replyToRef.current?.id;
      const fwdsToSend = [...fwdMsgs];

      const textDraft = inputRef.current;
      const filesDraft: DirectComposerPendingFile[] = composerFilesRef.current.map((item) => ({ ...item }));
      const projectsDraft: ChatAttachedProjectPayload[] = [...attachedProjectsRef.current];
      const sessionsDraft: ChatAttachedSessionPayload[] = [...attachedSessionsRef.current];
      const replyDraft: MessengerMessage | null = replyToRef.current;
      const forwardDraft: MessengerMessage[] = [...forwardMsgsRef.current];
      const referenceIdsDraft: ThreadComposerDraftReferenceIds = {
        replyToMessageId: draftReferenceIdsRef.current.replyToMessageId,
        forwardMessageIds: [...draftReferenceIdsRef.current.forwardMessageIds],
      };

      const restoreComposerAfterSendError = () => {
        draftReferenceIdsRef.current = referenceIdsDraft;
        setInput(textDraft);
        setComposerFiles(filesDraft);
        setAttachedProjects(projectsDraft);
        setAttachedSessions(sessionsDraft);
        setReplyToState(replyDraft);
        setForwardMsgsState(forwardDraft);
      };

      setIsSubmitting(true);
      setInput('');
      setComposerFiles([]);
      setAttachedProjects([]);
      setAttachedSessions([]);
      draftReferenceIdsRef.current = {
        replyToMessageId: null,
        forwardMessageIds: [],
      };
      setReplyToState(null);
      setForwardMsgsState([]);
      if (imageFileInputRef.current) imageFileInputRef.current.value = '';
      if (anyFileInputRef.current) anyFileInputRef.current.value = '';

      try {
        if (fwdsToSend.length > 0) {
          const ownPart: DirectThreadAtomicOwnPayload | undefined =
            text.trim() !== '' ||
            filesToSend.length > 0 ||
            ctxPayload.length > 0 ||
            replyId
              ? {
                  body: text || '',
                  ...(ctxPayload.length > 0 ? { context_attachments: ctxPayload } : {}),
                  ...(replyId ? { reply_to_id: replyId } : {}),
                  ...(filesToSend.length > 0 ? { localFile: filesToSend[0]! } : {}),
                }
              : undefined;

          const sentList = await sendOwnAndForwardedAtomic({
            own: ownPart,
            forwards: fwdsToSend.map((fwd) => {
              const ctxFwd = contextAttachmentsForForwardPayload(fwd.context_attachments);
              const hasFile =
                fwd.file_url != null &&
                typeof fwd.file_url === 'string' &&
                fwd.file_url.trim() !== '' &&
                fwd.file_url !== 'null';
              const label = fwd.forwarded_from_label?.trim();
              const apiPayload: DirectThreadForwardSendPayload = {
                body: fwd.body ?? '',
                ...(label ? { forwarded_from_label: label } : { forwarded_from_message_id: fwd.id }),
              };
              if (hasFile) {
                Object.assign(apiPayload, {
                  file_url: fwd.file_url,
                  file_name: fwd.file_name ?? undefined,
                  file_size: fwd.file_size ?? undefined,
                  mime_type: fwd.mime_type ?? undefined,
                  ...(fwd.image_width != null ? { image_width: fwd.image_width } : {}),
                  ...(fwd.image_height != null ? { image_height: fwd.image_height } : {}),
                });
              } else if (ctxFwd?.length) {
                apiPayload.context_attachments = ctxFwd;
              }
              return {
                forwarded_from_sender_name: label || resolveForwardAuthorName(fwd),
                apiPayload,
              };
            }),
          });
          const last = sentList[sentList.length - 1];
          if (last) {
            onMessageCommitted?.(last);
          }
          onConsumePendingForwardDraft?.();
        } else if (text || filesToSend.length > 0 || ctxPayload.length > 0) {
          const sent = await sendMessage({
            body: text || '',
            ...(ctxPayload.length > 0 ? { context_attachments: ctxPayload } : {}),
            ...(filesToSend.length > 0 ? { localImageFiles: filesToSend } : {}),
            ...(replyId ? { reply_to_id: replyId } : {}),
          });
          if (sent) {
            onMessageCommitted?.(sent);
          }
        }
        await clearPersistedDraft();
      } catch {
        restoreComposerAfterSendError();
      } finally {
        setIsSubmitting(false);
        requestAnimationFrame(() => {
          composerInputRef.current?.focus();
        });
      }
    },
    [
      sendMessage,
      sendOwnAndForwardedAtomic,
      resolveForwardAuthorName,
      editMessage,
      showToastError,
      composerInputRef,
      onMessageCommitted,
      onConsumePendingForwardDraft,
      clearPersistedDraft,
      restoreComposerDraftAfterEdit,
    ]
  );

  const handleImageFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files?.length ? Array.from(e.target.files) : [];
      e.target.value = '';
      if (files.length) addPhotosFromPicker(files);
    },
    [addPhotosFromPicker]
  );

  const handleAnyFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files?.length ? Array.from(e.target.files) : [];
      e.target.value = '';
      if (files.length) addFilesAsFilePicker(files);
    },
    [addFilesAsFilePicker]
  );

  const removeAttachedProject = useCallback((projectId: string) => {
    setAttachedProjects((prev) => prev.filter((p) => p.id !== projectId));
  }, []);

  const removeAttachedSession = useCallback((sessionId: string) => {
    setAttachedSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (editingMessageRef.current) return;
      if (e.dataTransfer.files.length > 0) {
        const all = Array.from(e.dataTransfer.files);
        if (all.length > 1) {
          showToastError(MESSENGER_DM_ONLY_ONE_FILE_ALLOWED);
          return;
        }
        if (composerFilesRef.current.length > 0) {
          showToastError(MESSENGER_DM_SINGLE_FILE_SLOT_OCCUPIED);
          return;
        }
        if (blockComposerAttachIfForwardDraft()) return;
        setAttachedProjects([]);
        setAttachedSessions([]);
        const images = all.filter((f) => f.type.startsWith('image/'));
        const nonImages = all.filter((f) => !f.type.startsWith('image/'));
        if (nonImages.length) {
          addFilesAsFilePicker(nonImages);
        } else if (images.length) {
          queueImagesForSendModeChoice(images);
        }
        return;
      }
      const incoming = readChatAttachFromDataTransfer(e.dataTransfer);
      if (incoming.projects.length === 0 && incoming.sessions.length === 0) return;
      if (blockComposerAttachIfForwardDraft()) return;
      setComposerFiles([]);
      setPendingImagePick(null);
      const next = mergeChatContextAttachOneSlot(
        attachedProjectsRef.current,
        attachedSessionsRef.current,
        incoming
      );
      setAttachedProjects(next.projects);
      setAttachedSessions(next.sessions);
    },
    [addFilesAsFilePicker, queueImagesForSendModeChoice, showToastError, blockComposerAttachIfForwardDraft]
  );

  return {
    teleport,
    input,
    setInput,
    handleInputChange,
    attachedProjects,
    attachedSessions,
    composerFiles,
    pendingImagePick,
    attachMenuOpen,
    setAttachMenuOpen,
    imageFileInputRef,
    anyFileInputRef,
    attachMenuRef,
    attachPopoverRef,
    attachMenuShouldRender,
    attachMenuContentClasses,
    attachMenuRequestClose,
    handleSubmit,
    handlePaste,
    handleImageFileSelect,
    handleAnyFileSelect,
    handleDrop,
    removeFile,
    removeAttachedProject,
    removeAttachedSession,
    handleImageSendModeChosen,
    dismissImageSendModeModal,
    replyTo,
    setReplyTo,
    clearReplyTo,
    forwardMsgs,
    setForwardMsgs,
    clearForwardMsgs,
    editingMessage,
    beginEditMessage,
    clearEditingMessage,
    handleComposerFocus,
    handleComposerBlur,
  };
}
